import { CARCASS_MM } from "./carcass";
import type { WardrobeModule } from "../types";
import { cabinetBaseSetbackMm } from "./installation";

// ---------------------------------------------------------------------------
// Угловой шкаф по картинке Сергея (согласовано 28.10)
//
// Угловой — один прямой корпус. Он заходит вглубь угла за соседний шкаф,
// стоящий у другой стены: эта глухая часть закрыта соседом, её глубина равна
// глубине соседнего шкафа с дверью и меняется сама вместе с ним. Перед глухой
// частью — добор в цвет фасадов (по выносу ручки), дальше одна дверь 400–600 мм.
// Внутри — только полки и штанги во всю длину.
//
// Петли (видео Сергея 31.10): у обеих дверей — у угла, «они открываются друг на
// друга». Поэтому доборов два: добор углового — между соседом и дверью углового
// (открытая дверь углового ложится вдоль фасада соседа), и добор соседа — на
// лице соседа у угла (его открытая дверь ложится вдоль фасада углового). Вместе
// они образуют угловой Г-образный добор.
//
// В угол уходит шкаф у задней стены или у боковой — «как кому больше нравится»
// (Сергей, 28.10): cornerRun = "back" | "side".
//
// Раньше угловой был Г-образным из двух корпусов с двумя дверями, ещё раньше —
// пятигранным; сохранённые проекты открываются уже новым угловым.
//
// План, мм: x — вдоль задней стены от угла комнаты, z — от задней стены в
// комнату (вдоль боковой стены). У правого угла план зеркален. Вдоль самого
// корпуса: u — от угла комнаты к наружному торцу, v — от стены к фронту.

export const cornerSide = (module: Pick<WardrobeModule, "cornerSide">) => module.cornerSide === "left" ? "left" : "right";

export type CornerRun = "back" | "side";
/** Вдоль какой стены стоит угловой: «back» — у задней, «side» — у боковой. */
export const cornerRun = (module: Pick<WardrobeModule, "cornerRun">): CornerRun => module.cornerRun === "side" ? "side" : "back";

/** Зазор двери у торца корпуса. */
const DOOR_REVEAL_MM = 2;
/** Двери стоят как у распашного шкафа: в 3 мм перед корпусом, толщина 20 мм. */
export const CORNER_DOOR_GAP_MM = 3;
export const CORNER_DOOR_THICKNESS_MM = 20;
/** Дверь соседнего шкафа выступает перед его корпусом на столько, мм. */
export const CORNER_NEIGHBOUR_FRONT_MM = CORNER_DOOR_GAP_MM + CORNER_DOOR_THICKNESS_MM;
/** Стык между модулями в раскладке (WALL_GAP_MM в layout.ts). */
const CORNER_LAYOUT_JOINT_MM = 1;
/** Задняя стенка корпуса, как у прямых шкафов. */
export const CORNER_BACK_MM = 8;
/** Дверь углового: уже 400 смысла нет, шире 600 — тяжело петлям (видео Сергея 23.10). */
export const CORNER_DOOR_MIN_MM = 400;
export const CORNER_DOOR_MAX_MM = 600;
export const CORNER_DOOR_DEFAULT_MM = 600;
/** Добор между дверью и соседним шкафом: по выносу ручки; точный размер даёт технолог. */
export const CORNER_FILLER_MIN_MM = 20;
export const CORNER_FILLER_MAX_MM = 150;
export const CORNER_FILLER_DEFAULT_MM = 50;
/** Добор соседа у угла — те же пределы, по выносу ручки углового. */
export const CORNER_NEIGHBOUR_FILLER_DEFAULT_MM = 50;
/** Глухая часть — по глубине соседнего шкафа (400–600 мм) с его дверью. */
export const CORNER_BLIND_MIN_MM = 300;
export const CORNER_BLIND_MAX_MM = 700;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const finite = (value: unknown, fallback: number) => Number.isFinite(value) ? Math.round(Number(value)) : fallback;

export const cornerDoorWidthMm = (module: Pick<WardrobeModule, "cornerDoorWidth">) =>
  clamp(finite(module.cornerDoorWidth, CORNER_DOOR_DEFAULT_MM), CORNER_DOOR_MIN_MM, CORNER_DOOR_MAX_MM);

export const cornerFillerMm = (module: Pick<WardrobeModule, "cornerFiller">) =>
  clamp(finite(module.cornerFiller, CORNER_FILLER_DEFAULT_MM), CORNER_FILLER_MIN_MM, CORNER_FILLER_MAX_MM);

/** Добор соседа у угла, мм; у углового без дверей доборов нет. */
export const cornerNeighbourFillerMm = (module: Pick<WardrobeModule, "cornerNeighbourFiller" | "door">) =>
  module.door === "open" ? 0
    : clamp(finite(module.cornerNeighbourFiller, CORNER_NEIGHBOUR_FILLER_DEFAULT_MM), CORNER_FILLER_MIN_MM, CORNER_FILLER_MAX_MM);

/** Глухая часть: за соседним шкафом; пока соседа нет — как у шкафа той же глубины. */
export const cornerBlindMm = (module: Pick<WardrobeModule, "cornerBlind" | "depth">) =>
  clamp(finite(module.cornerBlind, Math.round(module.depth) + CORNER_NEIGHBOUR_FRONT_MM), CORNER_BLIND_MIN_MM, CORNER_BLIND_MAX_MM);

/**
 * Глухая часть за соседним шкафом: его глубина и дверь перед ним (у стеллажа —
 * накладные фасады), в пределах 300–700 мм.
 */
export const cornerBlindFor = (neighbour: Pick<WardrobeModule, "type" | "depth" | "door">) => {
  const front = neighbour.type === "shelving" ? 18 : neighbour.door === "open" ? 0 : CORNER_NEIGHBOUR_FRONT_MM;
  return clamp(Math.round(neighbour.depth) + front, CORNER_BLIND_MIN_MM, CORNER_BLIND_MAX_MM);
};

/** Длина корпуса вдоль стены: глухая часть, добор, дверь и зазор у торца. */
export const cornerLengthMm = (module: Pick<WardrobeModule, "cornerBlind" | "cornerFiller" | "cornerDoorWidth" | "depth">) =>
  cornerBlindMm(module) + cornerFillerMm(module) + cornerDoorWidthMm(module) + DOOR_REVEAL_MM;

/**
 * Плечи вдоль стен — так угловой видит раскладка: a — по задней стене от угла,
 * b — по боковой. Корпус у задней стены: a — его длина, b — его глубина с
 * дверью; у боковой — наоборот.
 */
export const cornerArms = (module: Pick<WardrobeModule, "cornerRun" | "cornerBlind" | "cornerFiller" | "cornerNeighbourFiller" | "cornerDoorWidth" | "depth" | "door">) => {
  const length = cornerLengthMm(module);
  // Сосед на другой стене встаёт торцом к лицу двери и добора, а не к корпусу:
  // иначе его дверь задевает добор. Раскладка ставит модули со стыком 1 мм —
  // плечо на 1 мм короче, и сосед встаёт вплотную к добору, без щели (29.10).
  // Перед соседом у угла — его добор (31.10): плечо длиннее на него.
  const front = module.door === "open" ? 0
    : CORNER_NEIGHBOUR_FRONT_MM - CORNER_LAYOUT_JOINT_MM + cornerNeighbourFillerMm(module);
  const depth = Math.round(module.depth) + front;
  return cornerRun(module) === "back" ? { a: length, b: depth } : { a: depth, b: length };
};

export const cornerReturnWidth = (module: WardrobeModule) =>
  Number.isFinite(module.cornerReturnWidth) ? module.cornerReturnWidth! : cornerArms(module).b;

/** Точка вдоль корпуса (u — от угла, v — от стены) в координатах плана. */
export const cornerToPlan = (module: Pick<WardrobeModule, "cornerRun">, u: number, v: number): [number, number] =>
  cornerRun(module) === "back" ? [u, v] : [v, u];

/** Контур в плане — прямоугольник корпуса. */
export const cornerPlan = (module: WardrobeModule) => {
  const length = cornerLengthMm(module);
  const d = Math.round(module.depth);
  const run = cornerRun(module);
  const { a, b } = run === "back" ? { a: length, b: d } : { a: d, b: length };
  return {
    a, b, d, run, length,
    blind: cornerBlindMm(module),
    filler: cornerFillerMm(module),
    neighbourFiller: cornerNeighbourFillerMm(module),
    door: cornerDoorWidthMm(module),
    area: length * d / 1_000_000,
    points: [[0, 0], [a, 0], [a, b], [0, b]] as [number, number][]
  };
};

/** Дверь вдоль корпуса, мм от угла комнаты: от добора до наружного торца. */
export const cornerDoorSpan = (module: WardrobeModule) => {
  const { blind, filler, length } = cornerPlan(module);
  const from = blind + filler;
  const to = length - DOOR_REVEAL_MM;
  return { from, to, width: to - from, center: (from + to) / 2 };
};

/** Добор в плоскости дверей: от соседнего шкафа до двери, мм вдоль корпуса. */
export const cornerFillerSpan = (module: WardrobeModule) => {
  const { blind, filler, d } = cornerPlan(module);
  return {
    from: blind, to: blind + filler, widthMm: filler,
    /** От стены до лица корпуса и до лица двери. */
    near: d + CORNER_DOOR_GAP_MM, face: d + CORNER_NEIGHBOUR_FRONT_MM,
    thicknessMm: CORNER_DOOR_THICKNESS_MM
  };
};

/**
 * Добор соседа (31.10): в плоскости двери соседа (u — от лица глухой части на
 * толщину двери), от плоскости дверей углового до двери соседа — и заходит за
 * торец добора углового, закрывая угол. Ширина детали — добор + 20 мм.
 */
export const cornerNeighbourFillerSpan = (module: WardrobeModule) => {
  const { blind, d } = cornerPlan(module);
  const widthMm = cornerNeighbourFillerMm(module);
  return {
    from: blind - CORNER_DOOR_THICKNESS_MM, to: blind, widthMm,
    /** От стены вдоль соседа: от плоскости корпуса углового до двери соседа. */
    near: d + CORNER_DOOR_GAP_MM, face: d + CORNER_NEIGHBOUR_FRONT_MM + widthMm,
    /** Ширина детали: по лицу добор и ещё толщина двери за добором углового. */
    partWidthMm: widthMm + CORNER_DOOR_THICKNESS_MM,
    thicknessMm: CORNER_DOOR_THICKNESS_MM
  };
};

/** Площадь доборов (углового и соседа) при высоте дверей heightMm, м². */
export const cornerFillerAreaM2 = (module: WardrobeModule, heightMm: number) => {
  const neighbour = cornerNeighbourFillerMm(module);
  return (cornerFillerMm(module) + (neighbour ? neighbour + CORNER_DOOR_THICKNESS_MM : 0)) * heightMm / 1_000_000;
};

/**
 * Петли — у угла, у добора (видео Сергея 31.10: «петли в углах стоят, они
 * открываются друг на друга»; раньше были у наружного торца). Сторона — как
 * видит человек перед дверью: −1 слева, +1 справа.
 */
export const cornerHingeSide = (module: WardrobeModule): -1 | 1 =>
  (cornerRun(module) === "back") === (cornerSide(module) === "right") ? 1 : -1;

/**
 * Створка — в том же виде, что у распашного шкафа (moduleRules): одна дверь,
 * петли у добора (у угла), ручка у наружного торца.
 */
export const cornerDoorLeaves = (module: WardrobeModule) => {
  const span = cornerDoorSpan(module);
  const hingeSide = cornerHingeSide(module);
  const leaf = (index: number) => ({
    index,
    sectionIndex: index,
    sectionLeafIndex: 0,
    centerMm: 0,
    widthMm: span.width,
    hingeSide,
    hingeApplication: "full-overlay" as const,
    supportOffsetMm: hingeSide * (span.width / 2 + DOOR_REVEAL_MM - CARCASS_MM / 2)
  });
  // Антресоль над угловым — своя дверь над дверью корпуса (индекс 1).
  return cornerMezzanineMm(module) ? [leaf(0), leaf(1)] : [leaf(0)];
};

/** Антресоль над угловым (картинка Сергея, п. 5): 300–1000 мм; 0 — без неё. */
export const CORNER_MEZZANINE_MIN_MM = 300;
export const CORNER_MEZZANINE_MAX_MM = 1000;
export const cornerMezzanineMm = (module: Pick<WardrobeModule, "type" | "cornerMezzanine">) =>
  module.type === "corner45" && Number.isFinite(module.cornerMezzanine) && module.cornerMezzanine! >= CORNER_MEZZANINE_MIN_MM
    ? clamp(Math.round(module.cornerMezzanine!), CORNER_MEZZANINE_MIN_MM, CORNER_MEZZANINE_MAX_MM) : 0;

export const cornerDescription = (module: WardrobeModule) =>
  `${cornerSide(module) === "left" ? "Левый" : "Правый"} угловой шкаф · у ${cornerRun(module) === "back" ? "задней" : "боковой"} стены · ${cornerLengthMm(module)} мм, дверь ${cornerDoorWidthMm(module)} мм`;

// ---------------------------------------------------------------------------
// Наполнение: только полки и штанги во всю длину, вглубь угла.

/** Полки отступают от плоскости дверей (петли, зазор). */
export const CORNER_FRONT_CLEARANCE_MM = 20;

const toPlanPoints = (module: WardrobeModule, points: [number, number][]) =>
  points.map(([u, v]) => cornerToPlan(module, u, v));

/**
 * Стойка под петли (31.10): петли двери — у угла, у добора; в прямом корпусе
 * там нет боковины, поэтому за добором стоит стойка 16 мм глубиной 100 мм от
 * крыши до дна, на ней — монтажные планки петель. Полки — с вырезом под неё.
 * null — у углового без дверей стойки нет.
 */
export const CORNER_HINGE_POST_DEPTH_MM = 100;
export const cornerHingePostSpan = (module: WardrobeModule) => {
  if (module.door === "open") return null;
  const { d } = cornerPlan(module);
  const door = cornerDoorSpan(module);
  // Ось монтажной планки — как у боковины: 8 мм за кромкой двери с зазором 2 мм.
  const from = door.from - DOOR_REVEAL_MM;
  return { from, to: from + CARCASS_MM, back: d - CORNER_HINGE_POST_DEPTH_MM, front: d };
};

/**
 * Глухая панель (31.10): закрывает лицо глухой части — от боковины у стены до
 * стойки под петли, в плоскости лица корпуса, от дна до крыши. Так делают
 * угловые шкафы с глухой частью (blind panel): полки по-прежнему уходят вглубь
 * угла, но спереди закрыты — в щель за доборами ничего не упадёт; достают
 * рукой через дверь. Вместе со стойкой — одна Г-образная деталь, на ней петли.
 */
export const cornerBlindPanelSpan = (module: WardrobeModule) => {
  const post = cornerHingePostSpan(module);
  if (!post) return null;
  return { from: CARCASS_MM, to: post.from, back: post.front - CARCASS_MM, front: post.front, widthMm: post.from - CARCASS_MM };
};

/**
 * Щель между лицом углового и боковиной соседа (за доборами): снаружи
 * шкафов, пустая; сверху её закрывает планка в декоре корпуса вровень с
 * крышами (31.10). В координатах корпуса: u — от стены до добора соседа,
 * v — от лица углового до соседа. null — без дверей щели нет.
 */
export const cornerGapCoverSpan = (module: WardrobeModule) => {
  if (module.door === "open") return null;
  const { blind, d, neighbourFiller } = cornerPlan(module);
  return {
    from: 0, to: blind - CORNER_DOOR_THICKNESS_MM,
    near: d, face: d + CORNER_NEIGHBOUR_FRONT_MM + neighbourFiller,
    lengthMm: blind - CORNER_DOOR_THICKNESS_MM,
    widthMm: CORNER_NEIGHBOUR_FRONT_MM + neighbourFiller
  };
};

/** Полка во всю длину корпуса, с отступом от дверей — в координатах плана; вырез под стойку петель. */
export const cornerShelfPlan = (module: WardrobeModule): [number, number][] => {
  const { length, d } = cornerPlan(module);
  const t = CARCASS_MM;
  const front = d - CORNER_FRONT_CLEARANCE_MM;
  const post = cornerHingePostSpan(module);
  if (!post) return toPlanPoints(module, [[t, CORNER_BACK_MM], [length - t, CORNER_BACK_MM], [length - t, front], [t, front]]);
  const notch = post.back - 2;
  return toPlanPoints(module, [[t, CORNER_BACK_MM], [length - t, CORNER_BACK_MM], [length - t, front],
    [post.to + 2, front], [post.to + 2, notch], [post.from - 2, notch], [post.from - 2, front], [t, front]]);
};

export const cornerShelfSize = (module: WardrobeModule) => {
  const { length, d } = cornerPlan(module);
  return {
    length: length - CARCASS_MM * 2,
    depth: d - CORNER_FRONT_CLEARANCE_MM - CORNER_BACK_MM,
    areaM2: (length - CARCASS_MM * 2) * (d - CORNER_FRONT_CLEARANCE_MM - CORNER_BACK_MM) / 1_000_000
  };
};

/**
 * Цоколь — рамка под корпусом, как у прямого шкафа: передняя планка в
 * setback от фронта, торцевые у торцов, задняя у стены. Координаты — план.
 */
export const cornerPlinthPlan = (module: WardrobeModule) => {
  const { length } = cornerPlan(module);
  const face = Math.round(module.depth) - cabinetBaseSetbackMm(module);
  const plank = (from: [number, number], to: [number, number]) => {
    const a = cornerToPlan(module, from[0], from[1]);
    const b = cornerToPlan(module, to[0], to[1]);
    return { from: a, to: b, length: Math.hypot(to[0] - from[0], to[1] - from[1]) };
  };
  return {
    front: plank([14, face], [length - 14, face]),
    endOuter: plank([length - 23, 0], [length - 23, face - CARCASS_MM / 2]),
    endCorner: plank([23, 0], [23, face - CARCASS_MM / 2]),
    back: plank([23 + CARCASS_MM / 2, 23], [length - 23 - CARCASS_MM / 2, 23])
  };
};

/** Опоры на ножках: по углам, у длинного корпуса — ещё пара посередине. */
export const cornerLegPoints = (module: WardrobeModule): [number, number][] => {
  const { length, d } = cornerPlan(module);
  const inset = 60;
  const us = length > 1400 ? [inset, length / 2, length - inset] : [inset, length - inset];
  return us.flatMap((u) => [cornerToPlan(module, u, inset), cornerToPlan(module, u, d - inset)]);
};

export const cornerPolygonAreaM2 = (points: [number, number][]) => Math.abs(points.reduce((sum, p, i) => {
  const next = points[(i + 1) % points.length]!;
  return sum + p[0] * next[1] - next[0] * p[1];
}, 0)) / 2_000_000;

export const cornerPolygonSize = (points: [number, number][]) => ({
  width: Math.max(...points.map(p => p[0])) - Math.min(...points.map(p => p[0])),
  depth: Math.max(...points.map(p => p[1])) - Math.min(...points.map(p => p[1]))
});

/**
 * Ось штанги во всю длину корпуса, вглубь угла (за соседний шкаф). Опоры — по
 * 29 мм с торцов. from/to — в координатах плана.
 */
export const cornerRailPlan = (module: WardrobeModule) => {
  const { length, d } = cornerPlan(module);
  const inner = length - CARCASS_MM * 2;
  const offset = d * 0.535 + 18;
  return {
    from: cornerToPlan(module, CARCASS_MM + 29, offset),
    to: cornerToPlan(module, length - CARCASS_MM - 29, offset),
    /** Просвет между боковинами. */
    inner,
    length: inner - 58,
    offset
  };
};

/** Плечики шире штанги на эту величину в каждую сторону. */
export const CORNER_HANGER_HALF_WIDTH_MM = 220;

/** Сколько места от оси штанги до дверей: у неглубокого корпуса плечики упрутся в двери. */
export const cornerRailFrontClearanceMm = (module: WardrobeModule) =>
  Math.round(module.depth - cornerRailPlan(module).offset);
